import { createContext, useContext } from 'react';
import type {
  EmployeeContext,
  OrganizationOverview,
  OrgRole,
  PlatformPermission,
} from '@traceio-org/platform-contracts';
import type { SettingsAbilities, SettingsSection } from './permissions';

/** The organization a settings page is about, and what the signed-in person may do there. */
export interface SettingsScope {
  organizationId: string;
  /** The workspace as me/access reported it (branding, products, sign-in hints). */
  context: EmployeeContext;
  /**
   * The organization as platform-api read it for this visit, with the viewer's membership as it is now. Null for a
   * help desk member (isHelpDeskOnly): platform-api answers them no overview, and only Help desk is shown to them.
   */
  overview: OrganizationOverview | null;
  /** The viewer's organization permissions (overview.viewer, read from the database for this visit). */
  permissions: readonly PlatformPermission[];
  roles: readonly OrgRole[];
  abilities: SettingsAbilities;
  /** The sections this person sees, in navigation order. */
  sections: readonly SettingsSection[];
  /** The signed-in person: nobody changes their own membership. */
  me: { userId: string; displayName: string; email?: string | undefined; membershipId: string | null };
  /** Every workspace this person may administer (for the organization switch). */
  administrable: readonly EmployeeContext[];
  /** Reads the overview again (after a change that moves its counts). */
  refreshOverview: () => void;
}

const SettingsScopeContext = createContext<SettingsScope | null>(null);

export const SettingsScopeProvider = SettingsScopeContext.Provider;

export function useSettingsScope(): SettingsScope {
  const scope = useContext(SettingsScopeContext);
  if (!scope) throw new Error('account-hub: a settings section rendered outside its organization');
  return scope;
}

/**
 * The overview, for the sections only administrators see (Overview, Members, Domains): SectionGuard never renders
 * them for a help desk member, whose scope has none.
 */
export function useOverview(): OrganizationOverview {
  const { overview } = useSettingsScope();
  if (!overview) throw new Error('account-hub: an administration section rendered without the overview');
  return overview;
}

export const SECTION_TITLES: Readonly<Record<SettingsSection, string>> = {
  overview: 'Overview',
  members: 'Members',
  invitations: 'Invitations',
  modules: 'Modules',
  domains: 'Domains',
  'help-desk': 'Help desk',
  audit: 'Audit log',
  billing: 'Billing',
};

export function settingsPath(organizationId: string, section?: SettingsSection): string {
  return `/settings/${encodeURIComponent(organizationId)}${section ? `/${section}` : ''}`;
}
